import {useLocation, useSearchParams, Location, NavigateOptions} from "react-router-dom";
import useAppNavigation from "./useAppNavigate";
import { useTranslation } from "react-i18next";
import StringUtilities from 'utilities/common-utilities/string-utilities';

const stringifyValue = (value: any) => {
    return StringUtilities.stringifyValue(value);
}

const parseValue = (value: string) => {
    return StringUtilities.parseValue(value);
}

export type LocationManagerData = {
    searchParams: {
        get: (key: string) => any | null,
        set: (key: string, value: any, navigateOptions?: NavigateOptions) => void,
        has: (key: string) => boolean,
        delete: (key: string, navigateOptions?: NavigateOptions) => void,
        append: (key: string, value: any, navigateOptions?: NavigateOptions) => void
    },
    modal: {
        open: (key: string, data?: any) => void,
        close: (key: string) => void,
        getData: (key: string) => any
    },
    location: Location,
    navigate: (route: string | number, params?: Record<string, string | number>, navigateOptions?: NavigateOptions) => void,
    t: (key: string, params?: Record<string, string | number>) => string
}

const useLocationManager = (): LocationManagerData => {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useAppNavigation();
    const [t] = useTranslation();
    const data = {
        searchParams: {
            get: (key: string) => parseValue(searchParams.get(key)!),
            set: (key: string, value: any, navigateOptions?: NavigateOptions) => {
                searchParams.set(key, stringifyValue(value));
                setSearchParams(searchParams, navigateOptions);
            },
            has: (key: string) => searchParams.has(key),
            delete: (key: string, navigateOptions?: NavigateOptions) => {
                searchParams.delete(key);
                setSearchParams(searchParams, navigateOptions);
            },
            append: (key: string, value: any, navigateOptions?: NavigateOptions) => {
                if(searchParams.has(key)) searchParams.delete(key);
                searchParams.append(key, stringifyValue(value));
                setSearchParams(searchParams, navigateOptions);
            }
        },
        modal: {
            open: (key: string, data?: any) => {
                searchParams.delete(key);
                searchParams.append(key, stringifyValue(data));
                setSearchParams(searchParams);
            },
            close: (key: string) => {
                searchParams.delete(key);
                setSearchParams(searchParams, {
                    replace: true
                });
            },
            getData: (key: string) => {
                if (searchParams.has(key)) {
                    const value = searchParams.get(key);
                    const data = value ? parseValue(value) : null;
                    return data;
                }
                return null;
            }
        },
        location,
        navigate,
        t
    }

    return data;
};

export default useLocationManager;
